/**
 * The ONE Passport control a Team Management row shows (Oct 20 Issue 05 / FD-08).
 *
 *  - exactly one available view and nothing unavailable → a single button that
 *    opens it;
 *  - several views, or any view this viewer may not open → one button that
 *    opens a small chooser listing every applicable view; unavailable views are
 *    shown disabled WITH their reason in visible text (a tooltip on a disabled
 *    button never reaches a touch screen);
 *  - no applicable view → the same button, marked unavailable, whose chooser
 *    says why.
 *
 * The options come from `resolveTeamPassportOptions`; this component decides
 * nothing about roles or access.
 */
import { useState } from 'react';
import { FileUser } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { TEAM_PASSPORT_REASONS, type TeamPassportOption } from '@/lib/passport/teamPassportViews';

export interface TeamPassportControlProps {
  /** Stable id for test hooks — the member's user id. */
  readonly memberUserId: string;
  /** For the accessible name ("Passport for Sam Tech"). */
  readonly memberName: string | null;
  readonly options: readonly TeamPassportOption[];
  readonly onOpen: (href: string) => void;
}

export function TeamPassportControl({ memberUserId, memberName, options, onOpen }: TeamPassportControlProps) {
  const [open, setOpen] = useState(false);
  const available = options.filter((o) => o.href);
  const who = memberName ?? 'this team member';
  const testId = `member-passport-${memberUserId}`;

  // The direct case: one view, nothing to explain.
  if (options.length === 1 && available.length === 1) {
    const only = available[0];
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => onOpen(only.href!)}
        title={`Open ${who}'s Passport (${only.label})`}
        aria-label={`Open ${who}'s Passport (${only.label})`}
        data-testid={testId}
        data-passport-mode="direct"
        data-passport-href={only.href!}
        className="min-h-11 gap-1.5 md:min-h-9"
      >
        <FileUser className="h-4 w-4" />
        <span>Passport</span>
      </Button>
    );
  }

  const noneAvailable = available.length === 0;
  const summaryReason = options.length === 0
    ? TEAM_PASSPORT_REASONS.noApplicableView
    : noneAvailable ? options[0].unavailableReason ?? undefined : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-haspopup="dialog"
          aria-disabled={noneAvailable ? true : undefined}
          title={noneAvailable ? `Passport unavailable: ${summaryReason}` : `Choose a Passport view for ${who}`}
          aria-label={noneAvailable ? `${who}'s Passport is unavailable` : `Choose a Passport view for ${who}`}
          data-testid={testId}
          data-passport-mode={noneAvailable ? 'unavailable' : 'choose'}
          className={cn('min-h-11 gap-1.5 md:min-h-9', noneAvailable && 'opacity-50')}
        >
          <FileUser className="h-4 w-4" />
          <span>Passport</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 max-w-[calc(100vw-2rem)] p-2"
        data-testid={`${testId}-menu`}
      >
        <p className="px-2 pb-1 pt-1 text-xs font-medium text-muted-foreground">
          {who}&rsquo;s Passport
        </p>
        {options.length === 0 ? (
          <p className="px-2 py-2 text-sm text-muted-foreground" data-testid={`${testId}-reason`}>
            {TEAM_PASSPORT_REASONS.noApplicableView}
          </p>
        ) : (
          <ul className="space-y-1">
            {options.map((o) => (
              <li key={o.view}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto min-h-11 md:min-h-9 w-full justify-start whitespace-normal py-2 text-left"
                  disabled={!o.href}
                  data-testid={`${testId}-view-${o.view}`}
                  data-passport-href={o.href ?? undefined}
                  onClick={() => {
                    if (!o.href) return;
                    setOpen(false);
                    onOpen(o.href);
                  }}
                >
                  {o.label}
                </Button>
                {o.unavailableReason && (
                  <p
                    className="px-3 pb-1 text-xs text-muted-foreground"
                    data-testid={`${testId}-view-${o.view}-reason`}
                  >
                    {o.unavailableReason}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
